// Genesis Appliance Repair · EN / ES translations
// Centralized strings consumed via LanguageContext

const t = {
  en: {
    nav: {
      home: "Home",
      about: "About",
      services: "Services",
      blog: "Blog",
      contact: "Contact",
      bookNow: "Book Service",
    },
    common: {
      callNow: "Call Now",
      bookOnline: "Book Online",
      learnMore: "Learn More",
      readMore: "Read more",
      backHome: "Back to home",
      ourServices: "Our Services",
      viewAllServices: "View all services",
      requestService: "Request Service",
      yearsServing: "Years serving the Big Island",
      serviceAreas: "Service Areas",
      operatingHours: "Operating Hours",
      followUs: "Follow us",
      monFri: "Monday to Friday",
      sat: "Saturday",
      sun: "Sunday",
      closedOnlineOnly: "Closed in person, open for online booking",
      poweredBy: "Family-owned and operated on the Big Island",
      proudlyServing: "Proudly serving",
      trustedBrands: "We service every major brand",
      seeAllAreas: "and surrounding communities",
      reachUs: "Reach us",
      anyTime: "We answer fast, every day, online.",
      embedPlaceholder:
        "GoHighLevel booking form will load here. Paste your GHL embed snippet inside this container.",
    },
    home: {
      eyebrow: "Big Island · Premium Appliance Care",
      heroTitle: "Reliable Home Appliance Repair on the Big Island",
      heroLead:
        "A family-owned, organic approach to caring for the appliances at the center of your home: refrigerators, washers, ovens, and more, across Kailua-Kona, Waimea, Captain Cook, and surrounding communities.",
      ctaPrimary: "Book a Repair",
      ctaSecondary: "Explore Services",
      sectionServicesEyebrow: "What we fix",
      sectionServicesTitle: "End-to-end care for every appliance in your home.",
      sectionServicesLead:
        "From a fridge that refuses to cool to an oven that won't heat, our certified technicians handle every case with calm, precision, and respect for your home.",
      faqEyebrow: "Common questions",
      faqTitle: "Answered, honestly.",
      faqs: [
        {
          q: "Do you service the entire Big Island?",
          a: "Yes. We cover Kailua-Kona, Holualoa, Kealakekua, Captain Cook, Ocean View, Waimea, and the surrounding communities. If you're nearby, just ask.",
        },
        {
          q: "Which brands do you repair?",
          a: "Every major brand including GE, Samsung, Whirlpool, Amana, Electrolux, Maytag, Bosch, and LG, plus most premium and built-in models.",
        },
        {
          q: "How quickly can you come out?",
          a: "Most service visits are scheduled within 24 to 48 hours. We also offer same-day appointments when our route allows.",
        },
        {
          q: "Do you offer a warranty on repairs?",
          a: "Yes. Every repair is backed by a written labor warranty and manufacturer parts warranty for peace of mind.",
        },
        {
          q: "Can I book outside of business hours?",
          a: "Absolutely. Sundays we're closed in person, but online booking is open 24/7, and you'll get a confirmation the next business morning.",
        },
      ],
      ctaBlockTitle: "An appliance acting up? We'll quietly take care of it.",
      ctaBlockLead:
        "Talk to a real technician, get a real diagnosis, and book the next available visit in under two minutes.",
    },
    about: {
      eyebrow: "Our story",
      title: "Family-owned. Big Island-rooted. Quietly obsessed with craft.",
      lead:
        "Genesis Appliance Repair was founded on a simple belief: your kitchen and laundry room deserve the same care as the rest of your home. From Kona to Waimea, we've built our reputation on calm, careful, expert work.",
      pillarTitle1: "Local, never outsourced",
      pillarBody1:
        "Every call is answered locally. Every truck is staffed by a technician who lives and works on the Big Island.",
      pillarTitle2: "Premium-grade tools",
      pillarBody2:
        "We use OEM parts and modern diagnostics so repairs last, not just for the season, but for years.",
      pillarTitle3: "Respect for your home",
      pillarBody3:
        "Shoes off, drop cloths down, everything cleaner than we found it. The Genesis standard.",
      valuesTitle: "What we stand for",
      values: [
        { k: "Integrity", v: "Honest pricing. No upsells, ever." },
        { k: "Craft", v: "Real diagnostics, not guesswork." },
        { k: "Aloha", v: "We treat every home as if it were ours." },
        { k: "Reliability", v: "On time, every time. If something changes, we'll tell you." },
      ],
    },
    services: {
      eyebrow: "Services",
      title: "Premium repair, calmly delivered.",
      lead:
        "Choose the appliance you need help with. Each service is performed by a certified technician using OEM-grade parts.",
      cardCta: "View service",
    },
    serviceData: {
      washer: {
        title: "Washer Repair",
        tagline: "Leaking, not spinning, off balance. Handled.",
        body:
          "Front-load, top-load, high-efficiency, or stacked. Our technicians diagnose and repair every washer with care, using OEM parts and a clean working method that respects your laundry space.",
        issues: [
          "Leaks from the drum or hose",
          "Drum won't spin or agitate",
          "Loud banging or excessive vibration",
          "Won't drain or won't fill",
          "Door or lid won't lock",
          "Error codes and control board issues",
        ],
      },
      dryer: {
        title: "Dryer Repair",
        tagline: "No heat, no tumble, no problem.",
        body:
          "Gas or electric, we restore your dryer to factory performance, with vent inspection included on every visit to keep your home safe and efficient.",
        issues: [
          "No heat or low heat",
          "Drum not tumbling",
          "Excessive drying times",
          "Burning smell or overheating",
          "Squealing or grinding noises",
          "Sensor and thermostat failures",
        ],
      },
      refrigerator: {
        title: "Refrigerator Repair",
        tagline: "Keeping your food, and your peace, perfectly cool.",
        body:
          "From compressor failures to ice maker headaches, our refrigerator service covers every brand and every configuration: French-door, side-by-side, built-in, and beverage centers.",
        issues: [
          "Not cooling or freezing",
          "Ice maker not producing ice",
          "Water dispenser issues",
          "Loud humming or clicking",
          "Frost build-up in the freezer",
          "Door seal and gasket replacement",
        ],
      },
      dishwasher: {
        title: "Dishwasher Repair",
        tagline: "Spotless plates, silent cycles.",
        body:
          "We diagnose drainage, heating, spray-arm, and sensor issues across every major dishwasher brand, returning your machine to a quiet, efficient finish.",
        issues: [
          "Won't drain or standing water",
          "Dishes coming out dirty",
          "Leaks under the unit",
          "Door latch and seal issues",
          "Heating element failures",
          "Control panel and error codes",
        ],
      },
      rangeOven: {
        title: "Range & Oven Repair",
        tagline: "Even heat, steady flame, predictable results.",
        body:
          "Gas ranges, electric cooktops, double ovens, and wall ovens, calibrated and repaired with safety-first protocols and OEM components.",
        issues: [
          "Burner won't ignite",
          "Oven not heating evenly",
          "Temperature calibration drift",
          "Control board failures",
          "Door hinge and glass replacement",
          "Self-clean and lock errors",
        ],
      },
    },
    sectionDeepDive: {
      whatWeFix: "What we fix",
      ourProcess: "Our process",
      processSteps: [
        {
          n: "01",
          t: "Diagnose",
          b: "We test, listen, and inspect. No guesswork.",
        },
        {
          n: "02",
          t: "Quote",
          b: "Transparent, flat-rate pricing before any work starts.",
        },
        {
          n: "03",
          t: "Repair",
          b: "OEM parts, calibrated tools, careful technique.",
        },
        {
          n: "04",
          t: "Verify",
          b: "We run your appliance with you to confirm the fix.",
        },
      ],
      bookCta: "Book this repair",
    },
    contact: {
      eyebrow: "Reach us",
      title: "Schedule a repair, or simply ask a question.",
      lead:
        "Use the form on the right to book directly into our calendar, or call us during business hours. We answer fast.",
      formTitle: "Book a service visit",
      formNote:
        "Secure online booking, powered by our GoHighLevel scheduler.",
    },
    blog: {
      eyebrow: "Field notes",
      title: "Local expertise, written down.",
      lead:
        "Honest, practical guides from our technicians, written for Big Island homeowners.",
      posts: [
        {
          tag: "Refrigerators",
          title:
            "Why your fridge runs warmer in a Kona kitchen (and the simple fix)",
          excerpt:
            "Tropical climates and high humidity are a quiet threat to refrigeration. Here's how we re-calibrate compressors for Hawaii homes.",
          date: "Feb 12, 2026",
        },
        {
          tag: "Washers",
          title: "5 sounds your washer makes that you should never ignore",
          excerpt:
            "From the gentle thunk to the metallic shriek, here's what each noise means and when it's time to book a technician.",
          date: "Jan 30, 2026",
        },
        {
          tag: "Dryers",
          title: "Saltwater air, lint, and the dryer vent rule for Waimea",
          excerpt:
            "Coastal living means more frequent vent maintenance. Here's the schedule we recommend, and the safety reason behind it.",
          date: "Jan 18, 2026",
        },
        {
          tag: "Dishwashers",
          title:
            "Why your dishwasher leaves a film on glassware (it's not the soap)",
          excerpt:
            "Big Island water chemistry plays a role. We walk through the three repairs that solve 90% of the cases we see.",
          date: "Jan 4, 2026",
        },
        {
          tag: "Range & Oven",
          title: "How to safely test if your oven temperature is accurate",
          excerpt:
            "A simple at-home test that tells you whether your oven needs a recalibration or a full service visit.",
          date: "Dec 22, 2025",
        },
        {
          tag: "Buying Guide",
          title: "When repair beats replacement: a Big Island rule of thumb",
          excerpt:
            "Most appliances aren't worth replacing. Our technicians share the heuristics they use on every service call.",
          date: "Dec 10, 2025",
        },
      ],
    },
    footer: {
      tagline:
        "Quietly luxurious appliance repair across the Big Island of Hawaii.",
      navTitle: "Navigate",
      servicesTitle: "Services",
      contactTitle: "Get in touch",
      rights: "All rights reserved.",
    },
    seo: {
      "/": {
        title: "Premium Appliance Repair Hawaii | Genesis Appliance Repair",
        description:
          "Professional home appliance repair services in Kona, Waimea, Captain Cook & surrounding areas. Fast, organic, and expert troubleshooting for major brands.",
      },
      "/about": {
        title: "About Genesis | Family-Owned Appliance Repair in Hawaii",
        description:
          "Discover the family-owned story behind Genesis Appliance Repair: premium service, certified technicians, and a quiet craft rooted in the Big Island of Hawaii.",
      },
      "/services": {
        title: "Appliance Repair Services | Genesis, Big Island Hawaii",
        description:
          "Washer, dryer, refrigerator, dishwasher, range & oven repair across Kona, Waimea, and Captain Cook. Certified technicians, OEM parts, organic service.",
      },
      "/services/washer": {
        title: "Professional Washer Repair in Kona & Waimea HI | Genesis",
        description:
          "Washing machine leaking or not spinning? Genesis Appliance Repair provides expert washer troubleshooting and fixes throughout the Big Island of Hawaii.",
      },
      "/services/dryers": {
        title: "Expert Dryer Repair Services Kona & Waimea HI | Genesis",
        description:
          "Dryer not heating or tumbling? Genesis Appliance Repair restores gas and electric dryers across the Big Island, with vent inspection included.",
      },
      "/services/refrigerator": {
        title: "Expert Refrigerator Repair Services Hawaii | Genesis",
        description:
          "Keep your food fresh. Reliable, quick refrigerator and freezer repair services in Kailua-Kona, Holualoa, and Captain Cook. Open for digital booking.",
      },
      "/services/dishwasher": {
        title: "Professional Dishwasher Repair Big Island HI | Genesis",
        description:
          "Dishwasher not draining or cleaning properly? Genesis Appliance Repair delivers expert dishwasher service across Kona, Waimea & Captain Cook.",
      },
      "/services/range-oven": {
        title: "Range & Oven Repair in Kona, Waimea HI | Genesis",
        description:
          "Burner won't light? Oven heating unevenly? Genesis Appliance Repair offers safe, certified range and oven repair across the Big Island of Hawaii.",
      },
      "/contact": {
        title: "Contact Genesis Appliance Repair | Big Island Hawaii",
        description:
          "Book an appliance repair on the Big Island. Call (808) 764-4304 or schedule online. Serving Kailua-Kona, Holualoa, Waimea, Captain Cook, and beyond.",
      },
      "/blog": {
        title: "Appliance Repair Blog | Tips from Hawaii's Genesis Technicians",
        description:
          "Practical guides, repair tips, and Big Island appliance advice from the certified technicians at Genesis Appliance Repair.",
      },
    },
  },

  es: {
    nav: {
      home: "Inicio",
      about: "Nosotros",
      services: "Servicios",
      blog: "Blog",
      contact: "Contacto",
      bookNow: "Reservar Servicio",
    },
    common: {
      callNow: "Llamar Ahora",
      bookOnline: "Reservar en línea",
      learnMore: "Más información",
      readMore: "Leer más",
      backHome: "Volver al inicio",
      ourServices: "Nuestros Servicios",
      viewAllServices: "Ver todos los servicios",
      requestService: "Solicitar Servicio",
      yearsServing: "Años sirviendo a la Isla Grande",
      serviceAreas: "Áreas de Servicio",
      operatingHours: "Horario de Atención",
      followUs: "Síguenos",
      monFri: "Lunes a Viernes",
      sat: "Sábado",
      sun: "Domingo",
      closedOnlineOnly: "Cerrado en persona, abierto para reservas en línea",
      poweredBy: "Empresa familiar en la Isla Grande",
      proudlyServing: "Atendiendo con orgullo",
      trustedBrands: "Reparamos todas las marcas principales",
      seeAllAreas: "y comunidades cercanas",
      reachUs: "Contáctenos",
      anyTime: "Respondemos rápido, todos los días, en línea.",
      embedPlaceholder:
        "Aquí cargará el formulario de reservas GoHighLevel. Pegue el código embed de GHL dentro de este contenedor.",
    },
    home: {
      eyebrow: "Isla Grande · Reparación Premium de Electrodomésticos",
      heroTitle:
        "Reparación Confiable de Electrodomésticos en la Isla Grande",
      heroLead:
        "Un enfoque familiar y orgánico para cuidar los electrodomésticos en el corazón de su hogar: refrigeradores, lavadoras, hornos y más, en Kailua-Kona, Waimea, Captain Cook y comunidades cercanas.",
      ctaPrimary: "Reservar una Reparación",
      ctaSecondary: "Ver Servicios",
      sectionServicesEyebrow: "Lo que reparamos",
      sectionServicesTitle:
        "Cuidado integral para cada electrodoméstico de su hogar.",
      sectionServicesLead:
        "Desde un refrigerador que no enfría hasta un horno que no calienta, nuestros técnicos certificados resuelven cada caso con calma, precisión y respeto por su hogar.",
      faqEyebrow: "Preguntas frecuentes",
      faqTitle: "Respondidas con honestidad.",
      faqs: [
        {
          q: "¿Atienden toda la Isla Grande?",
          a: "Sí. Cubrimos Kailua-Kona, Holualoa, Kealakekua, Captain Cook, Ocean View, Waimea y comunidades cercanas. Si está cerca, sólo pregunte.",
        },
        {
          q: "¿Qué marcas reparan?",
          a: "Todas las marcas principales incluyendo GE, Samsung, Whirlpool, Amana, Electrolux, Maytag, Bosch y LG, además de la mayoría de modelos premium y empotrados.",
        },
        {
          q: "¿Qué tan rápido pueden venir?",
          a: "La mayoría de visitas se agendan dentro de 24 a 48 horas. También ofrecemos citas el mismo día cuando nuestra ruta lo permite.",
        },
        {
          q: "¿Ofrecen garantía en las reparaciones?",
          a: "Sí. Cada reparación incluye garantía escrita de mano de obra y garantía de fábrica en las piezas.",
        },
        {
          q: "¿Puedo reservar fuera del horario laboral?",
          a: "Por supuesto. Los domingos estamos cerrados en persona, pero la reserva en línea está abierta 24/7, y recibirá confirmación al siguiente día hábil.",
        },
      ],
      ctaBlockTitle:
        "¿Algún electrodoméstico fallando? Lo solucionamos en silencio.",
      ctaBlockLead:
        "Hable con un técnico real, obtenga un diagnóstico real y reserve la próxima visita disponible en menos de dos minutos.",
    },
    about: {
      eyebrow: "Nuestra historia",
      title:
        "Familia. Raíces en la Isla Grande. Obsesionados con el oficio.",
      lead:
        "Genesis Appliance Repair se fundó con una convicción simple: su cocina y lavandería merecen el mismo cuidado que el resto de su casa. De Kona a Waimea, construimos nuestra reputación con trabajo calmo, cuidadoso y experto.",
      pillarTitle1: "Local, nunca subcontratado",
      pillarBody1:
        "Cada llamada se contesta localmente. Cada camión está atendido por un técnico que vive y trabaja en la Isla Grande.",
      pillarTitle2: "Herramientas premium",
      pillarBody2:
        "Usamos piezas OEM y diagnóstico moderno para que las reparaciones duren, no sólo por la temporada, sino por años.",
      pillarTitle3: "Respeto por su hogar",
      pillarBody3:
        "Zapatos fuera, lonas en el piso, todo más limpio que como lo encontramos. El estándar Genesis.",
      valuesTitle: "Nuestros valores",
      values: [
        { k: "Integridad", v: "Precios honestos. Sin ventas adicionales." },
        { k: "Oficio", v: "Diagnóstico real, no suposiciones." },
        { k: "Aloha", v: "Tratamos cada hogar como el nuestro." },
        { k: "Confianza", v: "A tiempo, siempre. Si algo cambia, le avisamos." },
      ],
    },
    services: {
      eyebrow: "Servicios",
      title: "Reparación premium, entregada con calma.",
      lead:
        "Elija el electrodoméstico que necesita reparar. Cada servicio es realizado por un técnico certificado utilizando piezas de calidad OEM.",
      cardCta: "Ver servicio",
    },
    serviceData: {
      washer: {
        title: "Reparación de Lavadoras",
        tagline: "Fugas, sin centrifugar, desbalanceada. Resuelto.",
        body:
          "Carga frontal, carga superior, alta eficiencia o apiladas. Nuestros técnicos diagnostican y reparan toda lavadora con cuidado, usando piezas OEM y un método limpio que respeta su área de lavandería.",
        issues: [
          "Fugas del tambor o manguera",
          "El tambor no gira o agita",
          "Ruidos fuertes o vibración excesiva",
          "No drena o no llena",
          "Puerta o tapa no cierra",
          "Códigos de error y tarjeta de control",
        ],
      },
      dryer: {
        title: "Reparación de Secadoras",
        tagline: "Sin calor, sin tambor, sin problema.",
        body:
          "Gas o eléctricas, restauramos su secadora al rendimiento de fábrica, con inspección de ventilación incluida en cada visita para mantener su hogar seguro y eficiente.",
        issues: [
          "Sin calor o calor bajo",
          "El tambor no gira",
          "Tiempos de secado excesivos",
          "Olor a quemado o sobrecalentamiento",
          "Chillidos o rechinidos",
          "Fallos de sensores y termostatos",
        ],
      },
      refrigerator: {
        title: "Reparación de Refrigeradores",
        tagline: "Manteniendo sus alimentos, y su tranquilidad, frescos.",
        body:
          "Desde fallos del compresor hasta problemas con la máquina de hielo, cubrimos cada marca y configuración: French-door, side-by-side, empotrados y centros de bebidas.",
        issues: [
          "No enfría o no congela",
          "Máquina de hielo no produce",
          "Problemas con el dispensador de agua",
          "Zumbido fuerte o clics",
          "Acumulación de escarcha",
          "Sello y empaque de puerta",
        ],
      },
      dishwasher: {
        title: "Reparación de Lavavajillas",
        tagline: "Platos impecables, ciclos silenciosos.",
        body:
          "Diagnosticamos problemas de drenaje, calentamiento, brazo aspersor y sensores en todas las marcas, devolviendo su máquina a un acabado silencioso y eficiente.",
        issues: [
          "No drena o agua estancada",
          "Platos salen sucios",
          "Fugas debajo de la unidad",
          "Sellos y pestillo de puerta",
          "Fallos del elemento calentador",
          "Panel de control y códigos de error",
        ],
      },
      rangeOven: {
        title: "Reparación de Estufas y Hornos",
        tagline: "Calor uniforme, llama estable, resultados predecibles.",
        body:
          "Estufas de gas, encimeras eléctricas, hornos dobles y de pared, calibrados y reparados con protocolos de seguridad y componentes OEM.",
        issues: [
          "Quemador no enciende",
          "El horno no calienta uniformemente",
          "Desviación de calibración de temperatura",
          "Fallos de tarjeta de control",
          "Bisagras y vidrio de puerta",
          "Errores de auto-limpieza y bloqueo",
        ],
      },
    },
    sectionDeepDive: {
      whatWeFix: "Lo que reparamos",
      ourProcess: "Nuestro proceso",
      processSteps: [
        {
          n: "01",
          t: "Diagnóstico",
          b: "Probamos, escuchamos e inspeccionamos. Sin adivinanzas.",
        },
        {
          n: "02",
          t: "Cotización",
          b: "Precios transparentes y fijos antes de comenzar.",
        },
        {
          n: "03",
          t: "Reparación",
          b: "Piezas OEM, herramientas calibradas, técnica cuidadosa.",
        },
        {
          n: "04",
          t: "Verificación",
          b: "Probamos el electrodoméstico con usted para confirmar.",
        },
      ],
      bookCta: "Reservar esta reparación",
    },
    contact: {
      eyebrow: "Contáctenos",
      title: "Agende una reparación o simplemente haga una pregunta.",
      lead:
        "Use el formulario a la derecha para reservar directamente en nuestro calendario, o llámenos durante el horario laboral. Respondemos rápido.",
      formTitle: "Reservar una visita",
      formNote:
        "Reserva en línea segura, impulsada por nuestro programador GoHighLevel.",
    },
    blog: {
      eyebrow: "Notas de campo",
      title: "Experiencia local, escrita.",
      lead:
        "Guías honestas y prácticas de nuestros técnicos, escritas para propietarios de la Isla Grande.",
      posts: [
        {
          tag: "Refrigeradores",
          title:
            "Por qué su refrigerador funciona más caliente en una cocina de Kona (y la solución simple)",
          excerpt:
            "El clima tropical y la humedad alta son una amenaza silenciosa para la refrigeración. Así recalibramos compresores para hogares de Hawái.",
          date: "12 Feb, 2026",
        },
        {
          tag: "Lavadoras",
          title: "5 sonidos de su lavadora que nunca debe ignorar",
          excerpt:
            "Desde el golpe suave hasta el chillido metálico, qué significa cada ruido y cuándo es hora de llamar a un técnico.",
          date: "30 Ene, 2026",
        },
        {
          tag: "Secadoras",
          title:
            "Aire salino, pelusa y la regla del ducto de secadora en Waimea",
          excerpt:
            "Vivir en la costa significa mantenimiento más frecuente de la ventilación. Aquí está el calendario que recomendamos.",
          date: "18 Ene, 2026",
        },
        {
          tag: "Lavavajillas",
          title:
            "Por qué su lavavajillas deja una película en la cristalería (no es el jabón)",
          excerpt:
            "La química del agua de la Isla Grande influye. Repasamos las tres reparaciones que resuelven el 90% de los casos.",
          date: "4 Ene, 2026",
        },
        {
          tag: "Estufas y Hornos",
          title:
            "Cómo probar de forma segura si la temperatura de su horno es precisa",
          excerpt:
            "Una prueba sencilla en casa que indica si su horno necesita recalibración o una visita de servicio.",
          date: "22 Dic, 2025",
        },
        {
          tag: "Guía de compra",
          title:
            "Cuándo reparar gana a reemplazar: una regla para la Isla Grande",
          excerpt:
            "La mayoría de los electrodomésticos no valen la pena reemplazar. Nuestros técnicos comparten las heurísticas que usan.",
          date: "10 Dic, 2025",
        },
      ],
    },
    footer: {
      tagline:
        "Reparación silenciosamente lujosa de electrodomésticos en toda la Isla Grande de Hawái.",
      navTitle: "Navegar",
      servicesTitle: "Servicios",
      contactTitle: "Contacto",
      rights: "Todos los derechos reservados.",
    },
    seo: {
      "/": {
        title:
          "Reparación Premium de Electrodomésticos en Hawái | Genesis",
        description:
          "Servicios profesionales de reparación de electrodomésticos en Kona, Waimea y áreas cercanas. Soporte rápido y experto para las marcas más importantes.",
      },
      "/about": {
        title: "Sobre Genesis | Reparación Familiar de Electrodomésticos en Hawái",
        description:
          "Descubra la historia familiar detrás de Genesis Appliance Repair: servicio premium, técnicos certificados y un oficio enraizado en la Isla Grande.",
      },
      "/services": {
        title: "Servicios de Reparación de Electrodomésticos | Genesis Hawái",
        description:
          "Lavadora, secadora, refrigerador, lavavajillas, estufa y horno en Kona, Waimea y Captain Cook. Técnicos certificados, piezas OEM, servicio orgánico.",
      },
      "/services/washer": {
        title: "Reparación de Lavadoras en Kona y Waimea HI | Genesis",
        description:
          "¿Su lavadora tiene fugas o no centrifuga? Genesis Appliance Repair ofrece servicio técnico experto en lavadoras en toda la Isla Grande de Hawái.",
      },
      "/services/dryers": {
        title: "Reparación de Secadoras en Kona y Waimea HI | Genesis",
        description:
          "¿Secadora sin calor o sin girar? Genesis Appliance Repair restaura secadoras de gas y eléctricas en toda la Isla Grande, con inspección de ducto incluida.",
      },
      "/services/refrigerator": {
        title: "Servicio Técnico de Refrigeradores en Hawái | Genesis",
        description:
          "Mantenga sus alimentos frescos. Servicio confiable y rápido de reparación de refrigeradores y congeladores en Kailua-Kona, Holualoa y Captain Cook.",
      },
      "/services/dishwasher": {
        title:
          "Reparación Profesional de Lavavajillas en la Isla Grande | Genesis",
        description:
          "¿Lavavajillas no drena o no limpia? Genesis Appliance Repair ofrece servicio experto en Kona, Waimea y Captain Cook.",
      },
      "/services/range-oven": {
        title: "Reparación de Estufas y Hornos en Kona, Waimea HI | Genesis",
        description:
          "¿Quemador no enciende? ¿Horno calienta desigual? Genesis Appliance Repair ofrece reparación segura y certificada en toda la Isla Grande.",
      },
      "/contact": {
        title:
          "Contactar a Genesis Appliance Repair | Isla Grande de Hawái",
        description:
          "Reserve una reparación en la Isla Grande. Llame al (808) 764-4304 o agende en línea. Kailua-Kona, Holualoa, Waimea, Captain Cook y más.",
      },
      "/blog": {
        title:
          "Blog de Reparación de Electrodomésticos | Técnicos Genesis Hawái",
        description:
          "Guías prácticas, consejos de reparación y asesoría para hogares de la Isla Grande, escritos por los técnicos certificados de Genesis.",
      },
    },
  },
};

export default t;

// helper for nested key access: tr(lang, "home.heroTitle")
export const tr = (lang, path) => {
  const parts = path.split(".");
  let cur = t[lang] || t.en;
  for (const p of parts) {
    if (cur && typeof cur === "object" && p in cur) cur = cur[p];
    else return path;
  }
  return cur;
};
